import {DataStatus, FormSubmitHandler, Separator} from 'platform/components';
import {Show, VStack} from 'platform/foundation';

import {
  useCreateCommentMutation,
  useDeleteCommentMutation,
  useGetCommentsQuery,
} from '@dms/api/core';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {CommentCardForm, CommentCardItem, CommentsCardInput, handleApiError} from '@dms/shared';

type DirectSaleCommentsTabProps = {
  directSaleId: string;
  canAddComment: boolean;
  canDeleteComment: boolean;
};

export function DirectSaleCommentsTab(props: DirectSaleCommentsTabProps) {
  const resourceId = EntityResourceIds.directSale;
  const recordId = props.directSaleId;
  const {data: comments, isFetching, isError} = useGetCommentsQuery({resourceId, recordId});
  const [createComment, {isLoading: isCreating}] = useCreateCommentMutation();
  const [deleteComment, {isLoading: isDeleting}] = useDeleteCommentMutation();
  const isLoading = isFetching || isCreating || isDeleting;

  const handleSubmit: FormSubmitHandler<CommentCardForm> = (values, _, reset) => {
    if (!props.canAddComment) {
      return Promise.resolve();
    }

    return createComment({
      resourceId,
      recordId,
      createCommentRequestBody: {text: values.comment},
    })
      .unwrap()
      .then(() => reset())
      .catch(handleApiError);
  };

  const handleDelete = (commentId: string) => {
    if (!props.canDeleteComment) {
      return;
    }

    return deleteComment({resourceId, recordId, commentId}).unwrap().catch(handleApiError);
  };

  return (
    <VStack spacing={4} grow={1} minHeight={0}>
      <div css={{flex: 1, overflow: 'auto', minHeight: 0}}>
        <DataStatus
          minHeight={35}
          isLoading={isLoading}
          isError={isError}
          isEmpty={!comments?.length}
          emptyMessage={i18n.t('page.comments.labels.noOneCommented')}
        >
          {comments?.map((comment, index) => (
            <VStack key={comment.id}>
              <CommentCardItem
                comment={comment}
                isDeleteCommentLoading={isDeleting}
                onDelete={props.canDeleteComment ? handleDelete : undefined}
              />
              <Show when={index !== (comments?.length ?? 0) - 1}>
                <Separator spacing={0} />
              </Show>
            </VStack>
          ))}
        </DataStatus>
      </div>
      <Show when={props.canAddComment}>
        <CommentsCardInput onSubmit={handleSubmit} isSending={isCreating} />
      </Show>
    </VStack>
  );
}
